import React, { useEffect, useMemo, useState } from "react";

const JOURNAL_BASE = process.env.REACT_APP_JOURNAL_API_BASE || "https://journal.thenutrafox.com";

const CATEGORIES = [
  "Origin & Farming",
  "Nutrition & Wellness",
  "Quality & Process",
  "Culture & Stories",
];

function toSlug(input) {
  return String(input || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function PostBlog() {
  const [adminKey, setAdminKey] = useState("");
  const [savedKey, setSavedKey] = useState("");

  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("all"); // all | published | draft
  const [posts, setPosts] = useState([]);

  // Create form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [tags, setTags] = useState(""); // comma separated
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const [featuredImageAlt, setFeaturedImageAlt] = useState("");

  const [authorName, setAuthorName] = useState("The NutraFox");
  const [authorDesignation, setAuthorDesignation] = useState("Editorial Team");
  const [authorLinkedin, setAuthorLinkedin] = useState("");
  const [authorBio, setAuthorBio] = useState("");

  const [readingTimeMins, setReadingTimeMins] = useState(5);
  const [status, setStatus] = useState("draft"); // draft | published

  // content (simple)
  const [content, setContent] = useState("");

  useEffect(() => {
    const k = localStorage.getItem("NUTRAFOX_JOURNAL_ADMIN_KEY") || "";
    setSavedKey(k);
    setAdminKey(k);
  }, []);

  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (savedKey) h["x-admin-key"] = savedKey;
    return h;
  }, [savedKey]);

  async function loadPosts() {
    try {
      setPostsLoading(true);
      setError("");

      const res = await fetch(
        `${JOURNAL_BASE}/api/posts?status=${encodeURIComponent(statusFilter)}`,
        { cache: "no-store" }
        );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Failed to load posts (${res.status}): ${txt.slice(0, 120)}`);
      }

      const data = await res.json();
      setPosts(data?.posts || []);
    } catch (e) {
      setError(e?.message || "Failed to load posts");
    } finally {
      setPostsLoading(false);
    }
  }

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  function saveKey() {
    const clean = adminKey.trim();
    localStorage.setItem("NUTRAFOX_JOURNAL_ADMIN_KEY", clean);
    setSavedKey(clean);
    alert("Admin key saved.");
  }

  async function createPost(e) {
    e.preventDefault();

    if (!savedKey) {
      alert("Admin key missing. Save it first.");
      return;
    }

    const cleanTitle = title.trim();
    const cleanContent = content.trim();
    if (!cleanTitle) return alert("Title is required");
    if (!cleanContent) return alert("Content is required");

    const finalSlug = (slug.trim() || toSlug(cleanTitle)).trim();

    const tagsArr = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      title: cleanTitle,
      slug: finalSlug,
      excerpt: excerpt.trim(),
      category: category.trim(),
      tags: tagsArr,
      featuredImage: {
        url: featuredImageUrl.trim(),
        alt: featuredImageAlt.trim(),
      },
      author: {
        name: authorName.trim() || "The NutraFox",
        designation: authorDesignation.trim(),
        linkedinUrl: authorLinkedin.trim(),
        bioShort: authorBio.trim(),
      },
      readingTimeMins: Number(readingTimeMins) || 5,
      status,
      content: cleanContent, // ✅ simplest mode (your post page supports this)
    };

    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${JOURNAL_BASE}/api/posts`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
        });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Create failed (${res.status}): ${txt.slice(0, 200)}`);
      }

      // reset small stuff
      setTitle("");
      setSlug("");
      setExcerpt("");
      setTags("");
      setFeaturedImageUrl("");
      setFeaturedImageAlt("");
      setContent("");
      setStatus("draft");
      setReadingTimeMins(5);

      alert("Post created!");
      await loadPosts();
    } catch (e) {
      setError(e?.message || "Failed to create post");
    } finally {
      setLoading(false);
    }
  }

  async function setPostStatus(postSlug, nextStatus) {
    if (!savedKey) return alert("Admin key missing.");
    try {
      const res = await fetch(
        `${JOURNAL_BASE}/api/posts/${encodeURIComponent(postSlug)}`,
        {
            method: "PATCH",
            headers,
            body: JSON.stringify({ status: nextStatus }),
        }
        );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Update failed (${res.status}): ${txt.slice(0, 120)}`);
      }
      await loadPosts();
    } catch (e) {
      alert(e?.message || "Failed to update status");
    }
  }

  async function deletePost(postSlug) {
    if (!savedKey) return alert("Admin key missing.");
    const ok = window.confirm(`Delete post "${postSlug}"? This cannot be undone.`);
    if (!ok) return;

    try {
      const res = await fetch(
        `${JOURNAL_BASE}/api/posts/${encodeURIComponent(postSlug)}`,
        {
            method: "DELETE",
            headers,
        }
        );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Delete failed (${res.status}): ${txt.slice(0, 120)}`);
      }
      await loadPosts();
    } catch (e) {
      alert(e?.message || "Failed to delete");
    }
  }

  return (
    <div className="admin-page">
      <h2 className="admin-title">Post Blog</h2>

      {/* Admin key */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ minWidth: 240 }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Admin API Key</label>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Paste ADMIN_KEY"
              style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
          </div>
          <button
            onClick={saveKey}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              background: "#1E5631",
              color: "white",
              border: "none",
              cursor: "pointer",
              height: 40,
              marginTop: 18,
            }}
          >
            Save Key
          </button>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 18 }}>
            Saved: {savedKey ? "✅ Yes" : "❌ No"}
          </div>
        </div>
      </div>

      {error ? (
        <div className="admin-card" style={{ border: "1px solid #ffcccc", color: "#b00020", marginBottom: 16 }}>
          {error}
        </div>
      ) : null}

      {/* Create form */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Create New Post</h3>

        <form onSubmit={createPost} style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slug.trim()) setSlug(toSlug(e.target.value));
              }}
              placeholder="Title *"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Slug (auto)"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
          </div>

          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Excerpt (short summary)"
            rows={2}
            style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
          />

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Tags (comma separated)"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
          </div>

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <input
              value={featuredImageUrl}
              onChange={(e) => setFeaturedImageUrl(e.target.value)}
              placeholder="Featured image URL"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
            <input
              value={featuredImageAlt}
              onChange={(e) => setFeaturedImageAlt(e.target.value)}
              placeholder="Featured image alt text"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
          </div>

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Author name"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
            <input
              value={authorDesignation}
              onChange={(e) => setAuthorDesignation(e.target.value)}
              placeholder="Author designation"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
            <input
              value={authorLinkedin}
              onChange={(e) => setAuthorLinkedin(e.target.value)}
              placeholder="Author LinkedIn URL"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />
          </div>

          <textarea
            value={authorBio}
            onChange={(e) => setAuthorBio(e.target.value)}
            placeholder="Author bio (short)"
            rows={2}
            style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
          />

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <input
              type="number"
              min={1}
              max={60}
              value={readingTimeMins}
              onChange={(e) => setReadingTimeMins(e.target.value)}
              placeholder="Reading time"
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            />

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "#1E5631",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              {loading ? "Posting..." : "Create Post"}
            </button>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Content * (plain text for now — later we can upgrade to blocks/editor)"
            rows={10}
            style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
          />
        </form>
      </div>

      {/* List posts */}
      <div className="admin-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ marginTop: 0 }}>Posts</h3>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            <button
              onClick={loadPosts}
              disabled={postsLoading}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "#3E8E41",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              {postsLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {posts.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No posts yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {posts.map((p) => (
              <div
                key={p.slug}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{p.title}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      /journal/{p.slug} • {p.status} • {p.category || "—"}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {p.status !== "published" ? (
                      <button
                        onClick={() => setPostStatus(p.slug, "published")}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          background: "#1E5631",
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Publish
                      </button>
                    ) : (
                      <button
                        onClick={() => setPostStatus(p.slug, "draft")}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          background: "#777",
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Unpublish
                      </button>
                    )}

                    <a
                      href={`/journal/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd" }}
                    >
                      View
                    </a>

                    <button
                      onClick={() => deletePost(p.slug)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid #ffcccc",
                        color: "#b00020",
                        background: "white",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {p.excerpt ? <div style={{ fontSize: 13, opacity: 0.85 }}>{p.excerpt}</div> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}